import { Modal } from "semantic-ui-react";

import { createFormHelper } from "lib/Form";
import { BrandGroupForm } from "modules/BrandsDashboard/components/BrandGroupModal/components";
import { useCreateBrandGroupMutation, useUpdateBrandGroupMutation } from "modules/BrandsDashboard/components/BrandGroupModal/hooks";
import "modules/BrandsDashboard/components/BrandModal/BrandModal.scss";
import { useBrandGroupsQuery, useUploadBrandLogoMutation } from "modules/BrandsDashboard/hooks";
import { isValidUrl } from "modules/BrandsDashboard/utils";

import type { BrandGroup } from "models";
import type { BrandGroupFormValues } from "modules/BrandsDashboard/components/BrandGroupModal/components";

type BrandGroupModalProps = {
  isOpen: boolean;
  brandGroup: BrandGroup | null;
  onClose: () => void;
};

export const isBrandGroupNameTaken = (brandGroups: BrandGroup[], { id, name }: Pick<BrandGroup, "id" | "name">) => {
  const trimmedGroupName = name.trim().toLowerCase();
  if (!trimmedGroupName) return false;

  return brandGroups.some(group => group.id !== id && group.name.trim().toLowerCase() === trimmedGroupName);
};

const FormHelper = createFormHelper<BrandGroupFormValues>();

export const BrandGroupModal = ({ isOpen, brandGroup, onClose }: BrandGroupModalProps) => {
  const { data: brandGroups = [] } = useBrandGroupsQuery();

  const { mutateAsync: createBrandGroup, isPending: isCreateBrandGroupPending } = useCreateBrandGroupMutation();
  const { mutateAsync: updateBrandGroup, isPending: isUpdateBrandGroupPending } = useUpdateBrandGroupMutation();
  const { mutateAsync: uploadBrandLogo, isPending: isUploadLogoPending } = useUploadBrandLogoMutation();

  const handleSubmit = async ({ logoFile, ...payload }: BrandGroupFormValues) => {
    const name = payload.name.trim();
    if (!name || isBrandGroupNameTaken(brandGroups, payload)) return;

    const areUrlsValid = [payload.servicebox_wo, payload.servicebox_vin, payload.tablet_service_button_url, payload.servicebox_wo_2].every(isValidUrl);
    if (!areUrlsValid) return;

    let logo = payload.logo;

    if (logoFile) {
      const formData = new FormData();
      formData.append("file", logoFile);
      formData.append("path", name);
      logo = await uploadBrandLogo(formData);
    }

    if (brandGroup !== null) await updateBrandGroup({ ...payload, name, logo });
    else await createBrandGroup({ ...payload, name, logo });
    onClose();
  };

  return (
    <Modal open={isOpen} size="large" className="BrandModal">
      <FormHelper.Form
        onSubmit={handleSubmit}
        defaultValues={{
          id: brandGroup?.id ?? -1,
          name: brandGroup?.name ?? "",
          country: brandGroup?.country ?? "",
          button_label: brandGroup?.button_label ?? "",
          tablet_service_button_url: brandGroup?.tablet_service_button_url ?? "",
          servicebox_wo: brandGroup?.servicebox_wo ?? "",
          servicebox_vin: brandGroup?.servicebox_vin ?? "",
          logo: brandGroup?.logo ?? "",
          color: brandGroup?.color ?? "",
          is_active: brandGroup?.is_active ?? false,
          logoFile: null,
          servicebox_wo_2: brandGroup?.servicebox_wo_2 ?? "",
          servicebox_wo_2_name: brandGroup?.servicebox_wo_2_name ?? ""
        }}
      >
        <BrandGroupForm isEdit={brandGroup !== null} isPending={isCreateBrandGroupPending || isUpdateBrandGroupPending || isUploadLogoPending} onClose={onClose} />
      </FormHelper.Form>
    </Modal>
  );
};
